import { useMemo, useState } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

function branchLabel(branch, inputType) {
    if (branch.label) return branch.label;
    if (inputType === 'boolean') {
        return branch.value === true || branch.value === 'true' ? 'Yes' : 'No';
    }
    return String(branch.value);
}

function nodeTitle(node) {
    if (!node) return '';
    if (node.title) return node.title;
    if (node.type === 'terminal') return node.result?.condition || node.id;
    if (node.type === 'action') {
        return (
            node.value ||
            node.shelfName ||
            node.catalogFieldName ||
            node.subflowTreeId ||
            node.actionKind ||
            node.id
        );
    }
    return node.prompt || node.id;
}

function actionEyebrow(node) {
    const kind = node.actionKind || 'productGrade';
    if (kind === 'variant') return 'Variant';
    if (kind === 'listingField') return 'Reassign SKU';
    if (kind === 'addToBundle') return 'Add to bundle';
    if (kind === 'catalogField') return 'Catalog field';
    if (kind === 'shelf') return 'Shelf';
    if (kind === 'subflow') return 'Wizard';
    return 'Product Grade';
}

function branchCollapseKey(parentId, idx) {
    return `branch:${parentId}:${idx}`;
}

function FlowNode({
    nodeId,
    nodes,
    selectedId,
    onSelect,
    collapsed,
    setCollapsed,
    depth = 0,
    isStart = false,
    visited = null,
    onReorderBranches,
}) {
    const node = nodes[nodeId];
    const [dragBranchIndex, setDragBranchIndex] = useState(null);
    if (!node) return null;

    // Prevent infinite recursion when next-step links form a cycle
    const seen = visited || new Set();
    if (seen.has(nodeId)) {
        return (
            <div className="insp-meta" style={{ margin: '4px 0 8px 28px' }}>
                (cycle — “{nodeTitle(node)}” already appears above)
            </div>
        );
    }
    const nextVisited = new Set(seen);
    nextVisited.add(nodeId);

    const isQuestion = node.type === 'question';
    const isAction = node.type === 'action' || node.type === 'terminal';
    const isCollapsed = Boolean(collapsed[nodeId]);
    const selected = selectedId === nodeId;
    const branchCount = (node.branches || []).length;
    const canReorderBranches = Boolean(onReorderBranches) && branchCount > 1;
    const actionContinues = isAction && node.nextNodeId && nodes[node.nextNodeId];

    const toggleNode = (e) => {
        e.stopPropagation();
        setCollapsed((prev) => ({
            ...prev,
            [nodeId]: !prev[nodeId],
        }));
    };

    const toggleBranch = (key) => (e) => {
        e.stopPropagation();
        setCollapsed((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const showChevron = (isQuestion && branchCount > 0) || actionContinues;

    return (
        <div className={`insp-flow-section ${isStart ? 'is-start' : ''}`}>
            <div className="insp-flow-section-head">
                {showChevron ? (
                    <button
                        type="button"
                        className="insp-flow-chevron"
                        onClick={toggleNode}
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
                    >
                        <ExpandMoreRoundedIcon
                            sx={{
                                fontSize: 22,
                                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.15s ease',
                            }}
                        />
                    </button>
                ) : (
                    <span className="insp-flow-chevron-spacer" />
                )}

                <button
                    type="button"
                    className={`insp-node-card ${isAction ? 'result' : ''} ${selected ? 'selected' : ''}`}
                    onClick={() => onSelect(nodeId)}
                >
                    <div className="eyebrow">
                        {isStart ? 'Start · ' : ''}
                        {isAction
                            ? `${actionEyebrow(node)}${node.nextNodeId ? ' · continues' : ''}`
                            : `Question · ${branchCount} answers`}
                    </div>
                    <div className="name">{nodeTitle(node)}</div>
                </button>
            </div>

            {isQuestion && !isCollapsed && (
                <div className="insp-flow-children">
                    {(node.branches || []).map((branch, idx) => {
                        const childId = branch.nextNodeId;
                        const bKey = branchCollapseKey(nodeId, idx);
                        const branchCollapsed = Boolean(collapsed[bKey]);
                        const label = branchLabel(branch, node.inputType);
                        const hasChild = Boolean(childId && nodes[childId]);

                        return (
                            <div
                                key={`${nodeId}:${String(branch.value)}:${idx}`}
                                className={[
                                    'insp-flow-branch',
                                    dragBranchIndex === idx ? 'is-dragging' : '',
                                    dragBranchIndex != null && dragBranchIndex !== idx
                                        ? 'is-drop-target'
                                        : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                onDragOver={(e) => {
                                    if (!canReorderBranches) return;
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                    if (!canReorderBranches) return;
                                    e.preventDefault();
                                    const fromRaw = e.dataTransfer.getData('text/plain');
                                    const fromIndex =
                                        fromRaw !== '' ? Number(fromRaw) : dragBranchIndex;
                                    onReorderBranches(nodeId, fromIndex, idx);
                                    setDragBranchIndex(null);
                                }}
                            >
                                <div className="insp-flow-branch-head">
                                    {canReorderBranches ? (
                                        <span
                                            className="insp-flow-branch-drag"
                                            title="Drag to reorder answer"
                                            draggable
                                            onDragStart={(e) => {
                                                setDragBranchIndex(idx);
                                                e.dataTransfer.effectAllowed = 'move';
                                                e.dataTransfer.setData('text/plain', String(idx));
                                            }}
                                            onDragEnd={() => setDragBranchIndex(null)}
                                        >
                                            <DragIndicatorIcon fontSize="small" />
                                        </span>
                                    ) : null}
                                    <button
                                        type="button"
                                        className="insp-flow-branch-toggle"
                                        onClick={toggleBranch(bKey)}
                                        aria-expanded={!branchCollapsed}
                                    >
                                        <ExpandMoreRoundedIcon
                                            sx={{
                                                fontSize: 18,
                                                color: '#5C6570',
                                                transform: branchCollapsed
                                                    ? 'rotate(-90deg)'
                                                    : 'rotate(0deg)',
                                                transition: 'transform 0.15s ease',
                                            }}
                                        />
                                        <span>If {label}</span>
                                    </button>
                                </div>

                                {!branchCollapsed && (
                                    <div className="insp-flow-branch-body">
                                        {hasChild ? (
                                            <FlowNode
                                                nodeId={childId}
                                                nodes={nodes}
                                                selectedId={selectedId}
                                                onSelect={onSelect}
                                                collapsed={collapsed}
                                                setCollapsed={setCollapsed}
                                                depth={depth + 1}
                                                visited={nextVisited}
                                                onReorderBranches={onReorderBranches}
                                            />
                                        ) : (
                                            <div className="insp-meta" style={{ marginLeft: 4, marginBottom: 8 }}>
                                                (no next step)
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {actionContinues && !isCollapsed && (
                <div className="insp-flow-children">
                    <div className="insp-flow-branch">
                        <div className="insp-flow-branch-toggle" style={{ cursor: 'default' }}>
                            <span>Then</span>
                        </div>
                        <div className="insp-flow-branch-body">
                            <FlowNode
                                nodeId={node.nextNodeId}
                                nodes={nodes}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                collapsed={collapsed}
                                setCollapsed={setCollapsed}
                                depth={depth + 1}
                                visited={nextVisited}
                                onReorderBranches={onReorderBranches}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function FlowPane({
    tree,
    selectedId,
    onSelect,
    collapsed,
    setCollapsed,
    onAddResult,
    onAddQuestion,
    onReorderBranches,
}) {
    const nodes = tree?.nodes || {};
    const startId = tree?.startNodeId;

    const allCollapseKeys = useMemo(() => {
        const keys = [];
        for (const [id, node] of Object.entries(nodes)) {
            if (node?.type !== 'question') continue;
            keys.push(id);
            (node.branches || []).forEach((_, idx) => {
                keys.push(branchCollapseKey(id, idx));
            });
        }
        return keys;
    }, [nodes]);

    const expandAll = () => setCollapsed({});
    const collapseAll = () => {
        const next = {};
        for (const key of allCollapseKeys) {
            // Keep start question expanded so the tree still has a foothold
            if (key === startId) continue;
            next[key] = true;
        }
        setCollapsed(next);
    };

    return (
        <div className="insp-flow-pane">
            <div className="insp-flow-pane-header">
                <h3>Flow</h3>
                <div className="insp-flow-links">
                    <button type="button" onClick={expandAll}>Expand</button>
                    <button type="button" onClick={collapseAll}>Collapse</button>
                    {onAddQuestion && (
                        <button type="button" onClick={onAddQuestion}>+ Question</button>
                    )}
                    {onAddResult && (
                        <button type="button" onClick={onAddResult}>+ Result</button>
                    )}
                </div>
            </div>
            {startId && nodes[startId] ? (
                <FlowNode
                    nodeId={startId}
                    nodes={nodes}
                    selectedId={selectedId}
                    onSelect={onSelect}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    isStart
                    onReorderBranches={onReorderBranches}
                />
            ) : (
                <p className="insp-meta">No start node configured.</p>
            )}
        </div>
    );
}
